/* global Hls */
import { __awaiter } from "tslib";
/**
 * HLS support script
 */
import { debug, loadScriptTag, setStyles } from 'shared/utils';
import VideoBase from './videoBase';
const config = {
    capLevelToPlayerSize: true,
};
class VideoHLS extends VideoBase {
    constructor() {
        super(...arguments);
        this.pluginSetup = () => __awaiter(this, void 0, void 0, function* () {
            yield preSetupHLS();
            return new Promise(resolve => {
                this.player = this.videoPlaceholder;
                this.player.setAttribute('playsinline', ''); // iOS
                setStyles(this.player, {
                    height: '100%',
                    width: '100%',
                    objectFit: this.config.cover ? 'cover' : '',
                });
                if (Hls.isSupported()) {
                    const hls = new Hls(config);
                    this.hls = hls;
                    hls.attachMedia(this.player);
                    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                        // stop auto preload/buffering. Only trigger buffering on play
                        hls.config.autoStartLoad = false;
                        hls.loadSource(this.config.videoUrl);
                        hls.on(Hls.Events.MANIFEST_PARSED, () => {
                            this.mapPlaybackMethods();
                            resolve();
                        });
                    });
                    hls.on(Hls.Events.ERROR, (_event, data) => {
                        if (!data.fatal) {
                            return;
                        }
                        switch (data.type) {
                            case Hls.ErrorTypes.NETWORK_ERROR:
                                debug('network error encountered, try to recover');
                                hls.startLoad();
                                break;
                            case Hls.ErrorTypes.MEDIA_ERROR:
                                debug('media error encountered, try to recover');
                                hls.recoverMediaError();
                                break;
                            default:
                                // cannot recover
                                hls.destroy();
                                break;
                        }
                    });
                }
                else {
                    this.player.src = this.config.videoUrl;
                    this.player.addEventListener('loadedmetadata', () => {
                        this.mapPlaybackMethods();
                        resolve();
                    });
                }
            });
        });
    }
    /**
     * Map playback methods
     */
    mapPlaybackMethods() {
        this.player.addEventListener('durationchange', () => {
            this.duration = this.player.duration;
        });
        this.player.addEventListener('ended', () => {
            this.end();
        });
        this.getCurrentTime = () => this.player.currentTime;
        this.getVolume = () => this.player.volume * 100;
        this.setVolume = (volume) => {
            this.player.muted = volume === 0;
            this.player.volume = volume / 100;
        };
        this.isMuted = () => this.player.volume === 0;
        this.pluginMute = () => this.setVolume(0);
        this.pluginUnMute = () => this.setVolume(100);
        this.isPlaying = () => this.isPlayerPlaying;
        this.seekTo = (second) => {
            this.player.currentTime = second;
        };
        this.pluginPause = () => {
            this.player.pause();
            this.isPlayerPlaying = false;
        };
        this.pluginPlay = () => {
            var _a;
            // resume buffering
            (_a = this.hls) === null || _a === void 0 ? void 0 : _a.startLoad();
            // player play is a Promise
            // ensure to catch e to avoid error break from react.
            this.player.play().catch(e => {
                debug('Cannot play video', e);
            });
            this.isPlayerPlaying = true;
        };
    }
}
export default VideoHLS;
export const preSetupHLS = () => {
    // already set up previously. no need to do further
    if (window.HLSLoadPromise) {
        return window.HLSLoadPromise;
    }
    window.HLSLoadPromise = new Promise(resolve => {
        loadScriptTag('https://www.gannett-cdn.com/ads/scripts/hls.light.min.js').then(() => {
            resolve();
        });
    });
    return window.HLSLoadPromise;
};
